import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import DictionaryForm from "./DictionaryForm";
import NestedAccordion from "~/components/utils/NestedAccordion";
import DictionaryEditModal from "~/components/modal/DictionaryEditModal";
import {
  convertToObjectArray,
  DictionaryObject,
  getDictionary,
} from "~/TTS/voicevox/VoicevoxUtils";
import "~/App.css";
import "~/css/DictionaryList.css";
import { splitMoras } from "./HamburgerMenu/hooks/useDictionary";
import { useTranslation } from "react-i18next";
import { PlaySound } from "~/SoundUtils";
import { volumeAtom } from "~/Atom";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";

// アクセント表示
const renderPronunciationWithAccent = (pronunciation: string, accentType: string) => {
  const accent = Number(accentType);
  const moras = splitMoras(pronunciation);
  return (
    <div className="dict-accent-reading">
      {moras.map((m, idx) => {
        const moraIndex = idx + 1;
        const showDot = accent !== 0 && moraIndex === accent;
        return (
          <span key={idx} className={`dict-accent-char ${showDot ? "dict-accent-highlight" : ""}`}>
            {showDot && <span className="dict-accent-dot" />}
            {m}
          </span>
        );
      })}
    </div>
  );
};

const dictAnimation = {
  clearDelayMs: 10, // リスト初期化時の遅延時間(ミリ秒)
};

/**
 * 登録済みの辞書
 */
const DictionaryList: React.FC = () => {
  const { t } = useTranslation();
  const [volume] = useAtom(volumeAtom);
  const [dicts, setDicts] = useState<DictionaryObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDict, setSelectedDict] = useState<DictionaryObject | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const initList = async () => {
    setDicts([]);
    await new Promise((resolve) => setTimeout(resolve, dictAnimation.clearDelayMs));
    await fetchDictionary();
  };

  const fetchDictionary = async () => {
    setLoading(true);
    try {
      const customerId = await getCurrentCustomerId();
      const res = await getDictionary(customerId);
      const dictList: DictionaryObject[] = await convertToObjectArray(res);
      setDicts(dictList);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initList();
  }, []);

  const handleEdit = (dict: DictionaryObject) => {
    PlaySound("push", volume);
    setSelectedDict(dict);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setSelectedDict(null);
  };

  return (
    <>
      <div className="dictionary-area">
        <div className="registration-area">
          <NestedAccordion
            items={[
              {
                title: `${t("newDictionaryRegistration")}`,
                content: (
                  <DictionaryForm
                    registeredDicts={dicts}
                    onSaved={() => {
                      initList();
                    }}
                  />
                ),
                dynamicHeight: true,
                summaryClassName: "dict-create-accordion",
                defaultOpen: false,
              },
            ]}
          />
        </div>
        <div className="title registered-dict">{t("registeredDictionary")}</div>
        <div className="dict-table-wrapper">
          <div className="dict-table-header">
            <div className="dict-col word">{t("dictionaryWord")}</div>
            <div className="dict-col reading">{t("dictionaryReading")}</div>
            <div className="dict-col priority">{t("priority")}</div>
            <div className="dict-col actions"> </div>
          </div>
          <div className="dict-table-body">
            {loading && <div className="dict-row loading">{t("loading")}</div>}
            {!loading && dicts.length === 0 && (
              <div className="dict-row empty">{t("noRegisteredDictionary")}</div>
            )}
            {dicts.map((d) => (
              <div key={d.id} className="dict-row">
                <div className="dict-col word">{d.surface}</div>
                <div className="dict-col reading">
                  {renderPronunciationWithAccent(d.pronunciation || "", d.accent_type || "0")}
                </div>
                <div className="dict-col priority">{d.priority}</div>
                <div className="dict-col actions">
                  <button className="dict-edit-button" onClick={() => handleEdit(d)}>
                    {t("edit")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DictionaryEditModal
          dict={selectedDict}
          open={isEditOpen}
          onClose={closeEdit}
          onUpdated={initList}
          registeredDicts={dicts}
        />
      </div>
    </>
  );
};

export default DictionaryList;
